import DeleteSongMenuItem from "@/components/DeleteSongIcon";
import DropdownItem from "@/components/DropdownItem";
import SetListMenuItem from "@/components/SetListMenuItem";
import { useAppStore } from "@/store/app";
import { useReplicacheFriends, useReplicacheSongs } from "@/store/replicache";
import { getLyrics } from "@/utils/chordProMunge";
import { songToChordPro } from "@/utils/chordProToSong";
import fromNowOrNow from "@/utils/fromNowOrNow";
import slugify from "@/utils/slugify";
import type { Friends, Song, SongId, User } from "@/utils/types";
import { useParams } from "next/navigation";
import { MdCopyAll, MdFileDownload, MdMoreVert } from "react-icons/md";
import shallow from "zustand/shallow";
import Toast from "./Toast";

type MenuItems = "download";

export default function HeaderSongViewMoreMenu() {
  const { user } = useAppStore(({ user }) => ({ user }), shallow);
  const { friends } = useReplicacheFriends();
  const { songs } = useReplicacheSongs();
  const { songId } = useParams() as { songId: SongId };
  const song = songs[songId];

  const handleClickDownload = (key: MenuItems) => {
    //window.analyticsLogEvent("Clicked Editor Toolbar More Menu Item", { key });
    const el = document.createElement("a");
    el.setAttribute(
      "href",
      "data:text/plain;charset=utf-8," +
        encodeURIComponent(songToChordPro(song)),
    );
    const fileName =
      slugify(`${song.artist ? song.artist + "-" : ""}${song.title}`, false) +
      ".pro";
    el.setAttribute("download", fileName);

    el.style.display = "none";
    document.body.appendChild(el);
    el.click();
    document.body.removeChild(el);
  };

  const handleCopyLyrics = () => {
    if (!(song && song.chordPro)) return;
    const lyrics = getLyrics(song.chordPro);
    navigator.clipboard.writeText(lyrics);
  };

  //console.debug("HeaderSongViewMoreMenu", { song });

  return (
    <div className="dropdown dropdown-end">
      <label
        tabIndex={0}
        className="btn btn-circle btn-ghost text-2xl text-white"
      >
        <MdMoreVert />
      </label>
      {song && (
        <ul
          tabIndex={0}
          className="dropdown-content z-10 menu p-0 bg-base-300 rounded border-[1px] border-[#ffffff40]"
        >
          <SetListMenuItem />

          <DropdownItem onClick={() => handleClickDownload("download")}>
            <MdFileDownload className="text-2xl" />
            Download
          </DropdownItem>

          <DropdownItem onClick={handleCopyLyrics}>
            <MdCopyAll className="text-2xl" />
            <label htmlFor="copyLyrics">Copy Lyrics</label>
            <Toast id="copyLyrics">Copied link to clipboard!</Toast>
          </DropdownItem>

          <DeleteSongMenuItem />

          {user && (
            <li>
              <SongMetaInfo song={song} user={user} friends={friends} />
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

const SongMetaInfo = ({
  friends,
  song,
  user,
}: {
  friends: Friends;
  song: Song;
  user: User;
}) => {
  const creator =
    song.created_by_user_id === user.id
      ? user
      : friends[song.created_by_user_id];

  return (
    <p className="block text-xs">
      Created by <span title={creator?.id}>{creator?.displayName}</span>{" "}
      <span title={`${song.created_timestamp}`}>
        {fromNowOrNow(song.created_timestamp)}
      </span>
    </p>
  );
};
